import { HumanReadableError } from './errors';
import { fetchWithEdgeChallenge } from './edge-challenge';
import { transformApiSiteData, transformApiTiersData, getUrlHistory } from './helpers';
import { readMemberCustomFields } from './custom-fields';

function setupGhostApi({ siteUrl = window.location.origin, apiUrl, apiKey }) {
  const apiPath = 'members/api';

  function endpointFor({ type, resource }) {
    if (type === 'members') {
      return `${siteUrl.replace(/\/$/, '')}/${apiPath}/${resource}/`;
    }
  }

  function contentEndpointFor({ resource, params = {} }) {
    if (apiUrl && apiKey) {
      const searchParams = new URLSearchParams({
        ...params,
        key: apiKey,
      });
      return `${apiUrl.replace(/\/$/, '')}/${resource}/?${searchParams.toString()}`;
    }
    return '';
  }

  function makeRequest({
    url,
    method = 'GET',
    headers = {},
    credentials = undefined,
    body = undefined,
    edgeChallenge = false,
  }) {
    const options = {
      method,
      headers,
      credentials,
      body,
    };
    return edgeChallenge ? fetchWithEdgeChallenge(url, options) : fetch(url, options);
  }
  const api = {};

  api.site = {
    read() {
      const url = endpointFor({ type: 'members', resource: 'site' });
      return makeRequest({
        url,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }).then(function (res) {
        if (res.ok) {
          return res.json();
        } else {
          throw new Error('Failed to fetch site data');
        }
      });
    },

    newsletters() {
      const url = contentEndpointFor({ resource: 'newsletters', params: { limit: 100 } });
      return makeRequest({
        url,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }).then(function (res) {
        if (res.ok) {
          return res.json();
        } else {
          throw new Error('Failed to fetch site data');
        }
      });
    },

    tiers() {
      const url = contentEndpointFor({
        resource: 'tiers',
        params: { limit: 100, include: 'monthly_price,yearly_price,benefits' },
      });
      return makeRequest({
        url,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }).then(function (res) {
        if (res.ok) {
          return res.json();
        } else {
          throw new Error('Failed to fetch site data');
        }
      });
    },

    settings() {
      const url = contentEndpointFor({ resource: 'settings' });
      return makeRequest({
        url,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }).then(function (res) {
        if (res.ok) {
          return res.json();
        } else {
          throw new Error('Failed to fetch site data');
        }
      });
    },

    offer({ offerId }) {
      const url = contentEndpointFor({ resource: `offers/${offerId}` });
      return makeRequest({
        url,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }).then(function (res) {
        if (res.ok) {
          return res.json();
        } else {
          throw new Error('Failed to fetch offer data');
        }
      });
    },

    recommendations({ limit = 100 } = { limit: 100 }) {
      const url = contentEndpointFor({ resource: 'recommendations', params: { limit } });
      return makeRequest({
        url,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }).then(function (res) {
        if (res.ok) {
          return res.json();
        } else {
          throw new Error('Failed to fetch recommendations');
        }
      });
    },
  };

  api.feedback = {
    async add({ uuid, key, postId, score }) {
      let url = endpointFor({ type: 'members', resource: 'feedback' });
      if (uuid && key) {
        // only necessary if not logged in, and both are required if so
        url = url + `?uuid=${uuid}&key=${key}`;
      }
      const body = {
        feedback: [
          {
            post_id: postId,
            score,
          },
        ],
      };
      const res = await makeRequest({
        url,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      });
      if (res.ok) {
        return res.json();
      } else {
        throw (
          (await HumanReadableError.fromApiResponse(res)) ?? new Error('Failed to save feedback')
        );
      }
    },
  };

  const handleGiftResponse = async (res, fallbackMessage) => {
    if (res.ok) {
      return res.json();
    }

    const humanError = await HumanReadableError.fromApiResponse(res);
    if (humanError) {
      throw humanError;
    }

    throw new Error(fallbackMessage);
  };

  api.gift = {
    async fetchRedemptionData({ token }) {
      const url = endpointFor({
        type: 'members',
        resource: `gifts/${encodeURIComponent(token)}/redeem`,
      });
      const res = await makeRequest({
        url,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
      });

      return handleGiftResponse(res, 'Failed to load gift data');
    },

    async redeem({ token }) {
      const url = endpointFor({
        type: 'members',
        resource: `gifts/${encodeURIComponent(token)}/redeem`,
      });
      const res = await makeRequest({
        url,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({}),
      });

      return handleGiftResponse(res, 'Failed to redeem gift');
    },
  };

  api.recommendations = {
    trackClicked({ recommendationId }) {
      const url = endpointFor({
        type: 'members',
        resource: 'recommendations/' + recommendationId + '/clicked',
      });
      navigator.sendBeacon(url);
    },

    trackSubscribed({ recommendationId }) {
      const url = endpointFor({
        type: 'members',
        resource: 'recommendations/' + recommendationId + '/subscribed',
      });
      navigator.sendBeacon(url);
    },
  };

  api.member = {
    identity() {
      const url = endpointFor({ type: 'members', resource: 'session' });
      return makeRequest({
        url,
        credentials: 'same-origin',
      }).then(function (res) {
        if (!res.ok || res.status === 204) {
          return null;
        }
        return res.text();
      });
    },

    sessionData() {
      const url = endpointFor({ type: 'members', resource: 'member' });
      return makeRequest({
        url,
        credentials: 'same-origin',
      }).then(function (res) {
        if (!res.ok || res.status === 204) {
          return null;
        }
        return res.json();
      });
    },

    /**
     * The custom fields the publisher has opened to members, in the publisher's order.
     *
     * Portal reaches older Ghost sites indefinitely, so a site without the endpoint — or
     * a field that does not say what the member may do with it — reads as no fields.
     */
    customFields() {
      const url = endpointFor({ type: 'members', resource: 'member/metafields/custom' });
      return makeRequest({
        url,
        credentials: 'same-origin',
      }).then(function (res) {
        if (!res.ok) {
          return [];
        }
        // Read defensively: Portal and the site it talks to are deployed apart, so this
        // endpoint may be missing, or answered by a version that shapes it differently.
        // Anything unrecognisable reads as no fields, which is what a site with none
        // gives, rather than breaking the page these are drawn on.
        return res
          .json()
          .then((data) => {
            const fields = data?.members_metafields;
            if (!Array.isArray(fields)) {
              return [];
            }
            return readMemberCustomFields(fields);
          })
          .catch(() => []);
      });
    },

    update({
      name,
      subscribed,
      newsletters,
      enableCommentNotifications,
      enableUpdatesAndAnnouncements,
      metafields,
    }) {
      const url = endpointFor({ type: 'members', resource: 'member' });
      const body = {
        name,
        subscribed,
        newsletters,
      };
      if (metafields !== undefined) {
        body.metafields = metafields;
      }
      if (enableCommentNotifications !== undefined) {
        body.enable_comment_notifications = enableCommentNotifications;
      }
      if (enableUpdatesAndAnnouncements !== undefined) {
        body.enable_updates_and_announcements = enableUpdatesAndAnnouncements;
      }

      return makeRequest({
        url,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      }).then(async function (res) {
        if (!res.ok) {
          throw (
            (await HumanReadableError.fromApiResponse(res)) ?? new Error('Failed to update member')
          );
        }
        return res.json();
      });
    },

    deleteSuppression() {
      const url = endpointFor({ type: 'members', resource: 'member/suppression' });

      return makeRequest({
        url,
        method: 'DELETE',
      }).then(function (res) {
        if (!res.ok) {
          throw new Error('Your email has failed to resubscribe, please try again');
        }
        return true;
      });
    },

    async getIntegrityToken() {
      const url = endpointFor({ type: 'members', resource: 'integrity-token' });
      const res = await makeRequest({
        url,
        method: 'GET',
        edgeChallenge: true,
      });

      if (res.ok) {
        return res.text();
      } else {
        const humanError = await HumanReadableError.fromApiResponse(res);
        if (humanError) {
          throw humanError;
        }
        throw new Error('Failed to start a members session');
      }
    },

    /**
     * @returns {{
     *     inboxLinks?: {
     *         desktop: string;
     *         android: string;
     *         provider: 'gmail' | 'yahoo' | 'outlook' | 'proton' | 'icloud' | 'hey' | 'aol' | 'mailru';
     *     };
     *     otc_ref?: string;
     * }}
     */
    async sendMagicLink({
      email,
      emailType,
      labels,
      name,
      oldEmail,
      newsletters,
      redirect,
      integrityToken,
      phonenumber,
      customUrlHistory,
      token,
      giftToken,
      autoRedirect = true,
      includeOTC,
    }) {
      const url = endpointFor({ type: 'members', resource: 'send-magic-link' });
      const body = {
        name,
        email,
        newsletters,
        oldEmail,
        emailType,
        labels,
        requestSrc: 'portal',
        redirect,
        integrityToken,
        // we don't actually use a phone #, this is from a hidden field to prevent bot activity
        honeypot: phonenumber,
        token,
        giftToken,
        autoRedirect,
        includeOTC,
      };
      const urlHistory = customUrlHistory ?? getUrlHistory();
      if (urlHistory) {
        body.urlHistory = urlHistory;
      }

      const res = await makeRequest({
        url,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        edgeChallenge: true,
      });

      if (res.ok) {
        const contentType = (res.headers.get('content-type') || '').toLowerCase();
        if (contentType.includes('application/json')) {
          try {
            return await res.json();
          } catch (e) {
            // fall through to response used pre-OTC
          }
        }
        return {};
      } else {
        const humanError = await HumanReadableError.fromApiResponse(res);
        if (humanError) {
          throw humanError;
        }
        throw new Error('Failed to send magic link email');
      }
    },

    async verifyOTC({ otc, otcRef, redirect, integrityToken }) {
      const url = endpointFor({ type: 'members', resource: 'verify-otc' });
      const body = {
        otc,
        otcRef,
        redirect,
        integrityToken,
      };

      const res = await makeRequest({
        url,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        return await res.json();
      } else {
        const humanError = await HumanReadableError.fromApiResponse(res);
        if (humanError) {
          throw humanError;
        }
        throw new Error('Failed to verify code');
      }
    },

    signout(all = false) {
      const url = endpointFor({ type: 'members', resource: 'session' });
      return makeRequest({
        url,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          all,
        }),
      }).then(function (res) {
        if (res.ok) {
          window.location.replace(siteUrl);
          return 'Success';
        } else {
          throw new Error('Failed to signout');
        }
      });
    },

    async newsletters({ uuid, key }) {
      let url = endpointFor({ type: 'members', resource: `member/newsletters` });
      url = url + `?uuid=${uuid}&key=${key}`;
      return makeRequest({
        url,
        credentials: 'same-origin',
      }).then(function (res) {
        if (!res.ok || res.status === 204) {
          return null;
        }
        return res.json();
      });
    },

    async updateNewsletters({
      uuid,
      newsletters,
      key,
      enableCommentNotifications,
      enableUpdatesAndAnnouncements,
    }) {
      let url = endpointFor({ type: 'members', resource: `member/newsletters` });
      url = url + `?uuid=${uuid}&key=${key}`;
      const body = {
        newsletters,
      };

      if (enableCommentNotifications !== undefined) {
        body.enable_comment_notifications = enableCommentNotifications;
      }

      if (enableUpdatesAndAnnouncements !== undefined) {
        body.enable_updates_and_announcements = enableUpdatesAndAnnouncements;
      }

      return makeRequest({
        url,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      }).then(function (res) {
        if (res.ok) {
          return res.json();
        } else {
          throw new Error('Failed to update email preferences');
        }
      });
    },

    async updateEmailAddress({ email }) {
      const identity = await api.member.identity();
      const url = endpointFor({ type: 'members', resource: 'member/email' });
      const body = {
        email,
        identity,
      };

      return makeRequest({
        url,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      }).then(async function (res) {
        if (res.ok) {
          return 'Success';
        } else {
          const errData = await res.json();
          const errMssg =
            errData?.errors?.[0]?.message || 'Failed to send email address verification email';
          throw new Error(errMssg);
        }
      });
    },

    async checkoutPlan({
      plan,
      tierId,
      cadence,
      cancelUrl,
      successUrl,
      email: customerEmail,
      name,
      offerId,
      newsletters,
      metadata = {},
    } = {}) {
      const siteUrlObj = new URL(siteUrl);
      const identity = await api.member.identity();
      const url = endpointFor({ type: 'members', resource: 'create-stripe-checkout-session' });

      if (!successUrl) {
        const checkoutSuccessUrl = window.location.href.startsWith(siteUrlObj.href)
          ? new URL(window.location.href)
          : new URL(siteUrl);
        checkoutSuccessUrl.searchParams.set('stripe', 'success');
        successUrl = checkoutSuccessUrl.href;
      }
      if (!cancelUrl) {
        const checkoutCancelUrl = window.location.href.startsWith(siteUrlObj.href)
          ? new URL(window.location.href)
          : new URL(siteUrl);
        checkoutCancelUrl.searchParams.set('stripe', 'cancel');
        cancelUrl = checkoutCancelUrl.href;
      }
      const metadataObj = {
        name,
        newsletters: JSON.stringify(newsletters),
        requestSrc: 'portal',
        fp_tid: (window.FPROM || window.$FPROM)?.data?.tid,
        urlHistory: getUrlHistory(),
        ...metadata,
      };

      const body = {
        priceId: offerId ? null : plan,
        offerId,
        identity: identity,
        metadata: metadataObj,
        successUrl,
        cancelUrl,
      };

      if (customerEmail) {
        body.customerEmail = customerEmail;
      }

      if (tierId && cadence) {
        delete body.priceId;
        body.tierId = offerId ? null : tierId;
        body.cadence = offerId ? null : cadence;
      }
      return makeRequest({
        url,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })
        .then(async function (res) {
          if (!res.ok) {
            const errData = await res.json();
            const err = errData?.errors?.[0] || {};
            const errMssg = err.message || 'Failed to signup, please try again.';
            const error = new Error(errMssg);
            error.code = err.code;
            throw error;
          }
          return res.json();
        })
        .then(function (responseBody) {
          if (responseBody.url) {
            return window.location.assign(responseBody.url);
          }
          const stripe = window.Stripe(responseBody.publicKey);
          return stripe
            .redirectToCheckout({
              sessionId: responseBody.sessionId,
            })
            .then(function (redirectResult) {
              if (redirectResult.error) {
                throw new Error(redirectResult.error.message);
              }
            });
        });
    },

    async continueGiftCheckout() {
      const siteUrlObj = new URL(siteUrl);
      const identity = await api.member.identity();
      const url = endpointFor({ type: 'members', resource: 'create-stripe-checkout-session' });

      const checkoutSuccessUrl = window.location.href.startsWith(siteUrlObj.href)
        ? new URL(window.location.href)
        : new URL(siteUrl);
      checkoutSuccessUrl.searchParams.set('stripe', 'success');

      const checkoutCancelUrl = window.location.href.startsWith(siteUrlObj.href)
        ? new URL(window.location.href)
        : new URL(siteUrl);
      checkoutCancelUrl.searchParams.set('stripe', 'cancel');

      const body = {
        type: 'subscription',
        continueFromGift: true,
        identity,
        successUrl: checkoutSuccessUrl.href,
        cancelUrl: checkoutCancelUrl.href,
        metadata: {
          checkoutType: 'upgrade',
          requestSrc: 'portal',
          urlHistory: getUrlHistory(),
        },
      };

      return makeRequest({
        url,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })
        .then(async function (res) {
          if (!res.ok) {
            const errData = await res.json();
            const errMssg =
              errData?.errors?.[0]?.message ||
              'Failed to continue gift subscription, please try again.';
            throw new Error(errMssg);
          }
          return res.json();
        })
        .then(function (responseBody) {
          if (responseBody.url) {
            return window.location.assign(responseBody.url);
          }
          const stripe = window.Stripe(responseBody.publicKey);
          return stripe
            .redirectToCheckout({
              sessionId: responseBody.sessionId,
            })
            .then(function (redirectResult) {
              if (redirectResult.error) {
                throw new Error(redirectResult.error.message);
              }
            });
        });
    },

    async checkoutGift({
      tierId,
      cadence,
      duration,
      email: customerEmail,
      deliveryMethod,
      recipientEmail,
      recipientName,
      buyerName,
      personalMessage,
      deliveryDate,
    } = {}) {
      const siteUrlObj = new URL(siteUrl);
      const url = endpointFor({ type: 'members', resource: 'create-stripe-checkout-session' });

      let identity = null;
      try {
        identity = await api.member.identity();
      } catch (e) {
        // Not authenticated - that's fine for gift purchases
      }

      const cancelUrlObj = window.location.href.startsWith(siteUrlObj.href)
        ? new URL(window.location.href)
        : new URL(siteUrl);
      cancelUrlObj.hash = duration === undefined ? '#/portal/gift' : '#/portal/gift/delivery';

      const body = {
        identity,
        metadata: {
          requestSrc: 'portal',
        },
        type: 'gift',
        tierId,
        ...(duration === undefined ? { cadence } : { duration }),
        ...(deliveryMethod !== undefined ? { deliveryMethod } : {}),
        ...(recipientEmail ? { recipientEmail } : {}),
        ...(recipientName ? { recipientName } : {}),
        ...(buyerName ? { buyerName } : {}),
        ...(personalMessage ? { personalMessage } : {}),
        ...(deliveryDate ? { deliveryDate } : {}),
        cancelUrl: cancelUrlObj.href,
      };

      if (customerEmail) {
        body.customerEmail = customerEmail;
      }

      const response = await makeRequest({
        url,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      let responseJson = {};
      try {
        responseJson = await response.json();
      } catch (e) {
        // response may not be JSON (e.g. HTML error page from proxy)
      }

      if (!response.ok) {
        const error = responseJson?.errors?.[0];

        if (error) {
          throw error;
        }

        throw new Error('Failed to process gift checkout, please try again.');
      }

      if (responseJson.url) {
        return window.location.assign(responseJson.url);
      }

      throw new Error('Failed to process gift checkout, please try again.');
    },

    async checkoutDonation({ successUrl, cancelUrl, metadata = {}, personalNote = '' } = {}) {
      const identity = await api.member.identity();
      const url = endpointFor({ type: 'members', resource: 'create-stripe-checkout-session' });

      const metadataObj = {
        fp_tid: (window.FPROM || window.$FPROM)?.data?.tid,
        urlHistory: getUrlHistory(),
        ...metadata,
      };

      const body = {
        identity,
        metadata: metadataObj,
        successUrl,
        cancelUrl,
        type: 'donation',
        personalNote,
      };

      const response = await makeRequest({
        url,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const responseJson = await response.json();

      if (!response.ok) {
        const error = responseJson?.errors?.[0];
        if (error) {
          throw error;
        }

        throw new Error("We're unable to process your payment right now. Please try again later.");
      }

      return responseJson;
    },

    async editBilling({ successUrl, cancelUrl, subscriptionId } = {}) {
      const siteUrlObj = new URL(siteUrl);
      const identity = await api.member.identity();
      const url = endpointFor({ type: 'members', resource: 'create-stripe-update-session' });
      if (!successUrl) {
        const checkoutSuccessUrl = new URL(siteUrl);
        checkoutSuccessUrl.searchParams.set('stripe', 'billing-update-success');
        successUrl = checkoutSuccessUrl.href;
      }

      if (!cancelUrl) {
        const checkoutCancelUrl = window.location.href.startsWith(siteUrlObj.href)
          ? new URL(window.location.href)
          : new URL(siteUrl);
        checkoutCancelUrl.searchParams.set('stripe', 'billing-update-cancel');
        cancelUrl = checkoutCancelUrl.href;
      }
      return makeRequest({
        url,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identity: identity,
          subscription_id: subscriptionId,
          successUrl,
          cancelUrl,
        }),
      })
        .then(function (res) {
          if (!res.ok) {
            throw new Error('Unable to create stripe checkout session');
          }
          return res.json();
        })
        .then(function (result) {
          const stripe = window.Stripe(result.publicKey);
          return stripe.redirectToCheckout({
            sessionId: result.sessionId,
          });
        })
        .then(function (result) {
          if (result.error) {
            throw new Error(result.error.message);
          }
        })
        .catch(function (err) {
          throw err;
        });
    },

    async manageBilling({ returnUrl, subscriptionId } = {}) {
      const identity = await api.member.identity();
      const url = endpointFor({
        type: 'members',
        resource: 'create-stripe-billing-portal-session',
      });
      if (!returnUrl) {
        const returnUrlObj = new URL(siteUrl);
        returnUrlObj.searchParams.set('stripe', 'billing-portal-closed');
        returnUrl = returnUrlObj.href;
      }

      return makeRequest({
        url,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identity: identity,
          subscription_id: subscriptionId,
          returnUrl,
        }),
      })
        .then(function (res) {
          if (!res.ok) {
            throw new Error('Unable to create Stripe billing portal session');
          }
          return res.json();
        })
        .then(function (result) {
          return window.location.assign(result.url);
        })
        .catch(function (err) {
          throw err;
        });
    },

    async updateSubscription({
      subscriptionId,
      tierId,
      cadence,
      planId,
      smartCancel,
      cancelAtPeriodEnd,
      cancellationReason,
    }) {
      const identity = await api.member.identity();
      const url =
        endpointFor({ type: 'members', resource: 'subscriptions' }) + subscriptionId + '/';
      const body = {
        smart_cancel: smartCancel,
        cancel_at_period_end: cancelAtPeriodEnd,
        cancellation_reason: cancellationReason,
        identity: identity,
        priceId: planId,
      };

      if (tierId && cadence) {
        delete body.priceId;
        body.tierId = tierId;
        body.cadence = cadence;
      }

      // Thrown rather than returned: `fetch` treats a refusal as a response, so a
      // caller that only awaits this would carry on and tell the member their plan
      // changed when it did not.
      const res = await makeRequest({
        url,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        throw new Error('Failed to update subscription');
      }

      return res;
    },

    async offers() {
      const identity = await api.member.identity();
      const url = endpointFor({ type: 'members', resource: 'member/offers' });

      return makeRequest({
        url,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ identity }),
      })
        .then(function (res) {
          if (!res.ok) {
            return { offers: [] };
          }
          return res.json();
        })
        .catch(function () {
          return { offers: [] };
        });
    },

    async applyOffer({ offerId, subscriptionId }) {
      const identity = await api.member.identity();
      const url = endpointFor({
        type: 'members',
        resource: `subscriptions/${subscriptionId}/apply-offer`,
      });

      const res = await makeRequest({
        url,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identity,
          offer_id: offerId,
        }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || 'Failed to apply offer');
      }

      return true;
    },
  };

  api.init = async () => {
    const [member] = await Promise.all([api.member.sessionData()]);
    let site = {};
    let newsletters = [];
    let tiers = [];
    let settings = {};
    let offers = [];

    try {
      [{ settings }, { tiers }, { newsletters }] = await Promise.all([
        api.site.settings(),
        api.site.tiers(),
        api.site.newsletters(),
      ]);
      site = {
        ...settings,
        newsletters,
        tiers: transformApiTiersData({ tiers }),
      };
    } catch (e) {
      // Ignore
    }

    if (member && member.paid) {
      try {
        const offersData = await api.member.offers();

        offers = offersData.offers || [];
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[Portal] Failed to load member offers:', e);
      }
    }

    site = transformApiSiteData({ site });

    return { site, member, offers };
  };

  return api;
}

export default setupGhostApi;
